<template>
  <teleport v-if="visible" to="#modal-wrapper">
    <!-- Session Expired Modal Takeover -->
    <x-modal size="small" class="session-expired-modal">
      <div class="x-modal__contents">
        <ys-text
          v-if="!isLoggingIn"
          type="title3"
          class="session-expired-title"
        >
          Welcome back
        </ys-text>
        <ys-text v-if="isLoggingIn" type="title3" class="session-expired-title">
          We're signing you in...
        </ys-text>
        <p v-if="!isLoggingIn" class="session-expired-copy">
          To help keep your account secure, please sign in again to continue
        </p>
        <p v-if="isLoggingIn" class="session-expired-copy">
          Checking credentials
          <x-loading-spinner />
        </p>
        <x-onx-sso-button
          v-if="!isLoggingIn"
          text="Log in with onX"
          @click="signinAgain"
        />
      </div>
    </x-modal>
  </teleport>
</template>

<!-- eslint-disable vue/block-lang -->
<script setup>
import xModal from '@/components/shared/x-modal'
import '@onxmaps/yellowstone-ui/ys-text'
import xOnxSsoButton from '@/components/x-onx-sso-button'
import xLoadingSpinner from '@/components/shared/x-loading-spinner/index.vue'
import { signinPopup } from '@/services/identity-client'
import pubsub from '@/lib/pubsub'
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { info, error, warn } from '@/lib/logger'
import { log } from '@/services/logging-service'
import { useUser } from '@/services/identity-vue-provider'

const visible = ref(false)
const isLoggingIn = ref(true)
const user = useUser()
const route = useRoute()

function show() {
  // Don't show the modal if it's already visible or if we're on a pre auth route
  if (visible.value || route?.meta?.preAuthRoute) {
    return
  }

  // Show the loading spinner for 4 seconds then the login button.
  // When returning users visit the site after their session has expired,
  // it can take a few seconds for the identity client to check if they need to
  // do a redirect to sign in again. During this time, we show a loading spinner to indicate something is happening.
  // After 4 seconds, we show the login button so they can sign in again if needed.
  isLoggingIn.value = true
  setTimeout(() => {
    isLoggingIn.value = false
  }, 4000)
  visible.value = true
}

function hide() {
  visible.value = false
}

watch(user, (newValue) => {
  if (newValue.hasUserSessionExpired) {
    show()
  } else {
    hide()
  }
})

async function signinAgain() {
  isLoggingIn.value = true
  try {
    const user = await signinPopup()
    if (user) {
      info('Identity', 'User signed in successfully via popup')
      log('Identity SessionExpired User signed in successfully via popup')
    } else {
      warn('Identity', 'User failed to sign in via popup')
      log('Identity SessionExpired User failed to sign in via popup')
    }
  } catch (e) {
    error('Identity', 'Error logging identity error', e)
    log('Identity SessionExpired User failed to sign in via popup', e)
  }
  isLoggingIn.value = false
}

onMounted(() => {
  pubsub.subscribe('show_session_expired_modal', show)
  pubsub.subscribe('hide_session_expired_modal', hide)
})
onBeforeUnmount(() => {
  pubsub.unsubscribe('show_session_expired_modal', show)
  pubsub.unsubscribe('hide_session_expired_modal', hide)
})
</script>

<style lang="scss" scoped>
.session-expired-title {
  margin: 0 0 12px 0;
}

.session-expired-copy {
  margin: 0 0 20px 0;
}
</style>
